import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, IndianRupee, Plus, RefreshCw } from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import {
  apiMessage,
  advanceDuplicateFromError,
  approveAdvance,
  cancelAdvance,
  createAdvance,
  getAdvanceAllocations,
  getAdvanceOverview,
  getOrgAdvances,
  listEmployees,
  putAdvanceEligibility,
  putAdvanceSettings,
  rejectAdvance,
  updateAdvance,
} from "./allowancesApi";
import AdvanceCreateWizard from "./AdvanceCreateWizard";
import {
  ConfirmShell,
  Drawer,
  DuplicateRecordDialog,
  EmptyState,
  FeatureDisabledBanner,
  FeatureHeader,
  OrgToggle,
  PaginationBar,
  StatusBadge,
  SuccessFlash,
  SummaryCards,
  Tabs,
  currentMonthValue,
  formatDate,
  formatMoney,
  roleFlags,
  sameDate,
  sameMoney,
} from "./shared";
import "../../styles/allowances-benefits.css";
import "../../styles/allowances-benefits-advance-extra.css";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "employees", label: "Employees" },
  { id: "advances", label: "Advances" },
  { id: "recovery", label: "Recovery / History" },
];

const EMPTY_FORM = { employee_id: "", amount: "", advance_date: "", note: "" };
const PAGE_SIZE = 25;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function monthBounds(ym) {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m) return { date_from: undefined, date_to: undefined };
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const last = new Date(y, m, 0).getDate();
  const end = `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
  return { date_from: start, date_to: end };
}

function monthFromDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (raw.length < 7) return currentMonthValue();
  return raw.slice(0, 7);
}

async function findDuplicateAdvance({
  employeeId,
  advanceDate,
  amount,
  excludeId,
  localAdvances = [],
  approvedOnly = false,
}) {
  const matches = (row) => {
    if (!row) return false;
    if (excludeId && String(row.id) === String(excludeId)) return false;
    if (String(row.status || "").toLowerCase() === "cancelled") return false;
    if (String(row.employee_id) !== String(employeeId)) return false;
    if (!sameDate(row.advance_date, advanceDate)) return false;
    if (approvedOnly) {
      const approval = String(row.approval_status || "").toLowerCase();
      return approval === "approved";
    }
    return true;
  };

  const localHits = (localAdvances || []).filter(matches);
  if (localHits.length) {
    const amountHit = localHits.find((row) => sameMoney(row.amount, amount));
    return amountHit || localHits[0];
  }

  const dateKey = String(advanceDate || "").slice(0, 10);
  try {
    const data = await getOrgAdvances({
      employee_id: employeeId,
      date_from: dateKey,
      date_to: dateKey,
      limit: 200,
      offset: 0,
    });
    const rows = (data?.advances || []).filter(matches);
    if (!rows.length) return null;
    const amountHit = rows.find((row) => sameMoney(row.amount, amount));
    return amountHit || rows[0];
  } catch {
    return null;
  }
}

function shiftMonth(ym, delta) {
  const [y, m] = String(ym).split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(ym) {
  const [y, m] = String(ym).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function approvalOf(row) {
  const explicit = String(row?.approval_status || "").toLowerCase();
  if (explicit === "pending" || explicit === "approved" || explicit === "rejected") return explicit;
  return String(row?.status || "").toLowerCase() === "pending" ? "pending" : "";
}

function displayStatus(row) {
  const status = String(row?.status || "").toLowerCase();
  const approval = approvalOf(row);
  if (status === "pending" && approval === "approved") return "Approved";
  if (status === "pending") return "Pending approval";
  if (approval === "rejected" || status === "cancelled") return "Rejected";
  if (status === "applied") return "Applied";
  if (status === "closed") return "Closed";
  return status || "—";
}

function displayStatusKey(row) {
  const label = displayStatus(row);
  if (label === "Approved" || label === "Applied" || label === "Closed") return "approved";
  if (label === "Rejected") return "rejected";
  return "pending";
}

function needsApproval(row) {
  return String(row?.status || "").toLowerCase() === "pending" && approvalOf(row) !== "approved";
}

export default function AdvanceTakenPage() {
  const toast = useToast();
  const { user: me } = useContext(AuthContext);
  const role = me?.role || me?.user?.role || "";
  const { canManage } = roleFlags(role);

  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [overview, setOverview] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [advancesPayload, setAdvancesPayload] = useState({ total: 0, advances: [] });
  const [historyPayload, setHistoryPayload] = useState({ total: 0, advances: [] });
  const [filters, setFilters] = useState({
    status: "",
    employee_id: "",
    month: currentMonthValue(),
    day: "",
    offset: 0,
  });
  const [historyFilters, setHistoryFilters] = useState({ status: "", offset: 0 });
  const [empSearch, setEmpSearch] = useState("");
  const [showAllAlloc, setShowAllAlloc] = useState(false);
  const [calendarDays, setCalendarDays] = useState({}); // dayNumber -> count

  const [eligDrawer, setEligDrawer] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [successFlash, setSuccessFlash] = useState(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, alloc, empRes] = await Promise.all([
        getAdvanceOverview().catch(() => null),
        getAdvanceAllocations({ enabledOnly: !showAllAlloc }).catch(() => ({ allocations: [], enabled: false })),
        listEmployees({ active_only: true, page_size: 500 }).catch(() => []),
      ]);
      setOverview(ov);
      setEnabled(Boolean(ov?.enabled ?? alloc?.enabled));
      setAllocations(alloc?.allocations || []);
      const empList = Array.isArray(empRes) ? empRes : empRes?.items || [];
      setEmployees(empList);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load advance taken."));
    } finally {
      setLoading(false);
    }
  }, [showAllAlloc, toast]);

  const loadAdvances = useCallback(async () => {
    try {
      const bounds = monthBounds(filters.month);
      const data = await getOrgAdvances({
        limit: 500,
        offset: 0,
        status: filters.status || undefined,
        employee_id: filters.employee_id || undefined,
        date_from: bounds.date_from,
        date_to: bounds.date_to,
      });
      setEnabled(Boolean(data?.enabled ?? enabled));
      const all = data?.advances || [];
      const dayCounts = {};
      all.forEach((row) => {
        const day = String(row.advance_date || "").slice(8, 10);
        if (!day) return;
        const key = String(Number(day));
        dayCounts[key] = (dayCounts[key] || 0) + 1;
      });
      setCalendarDays(dayCounts);

      let rows = all;
      if (filters.day) {
        const d = String(filters.day).padStart(2, "0");
        rows = all.filter((row) => String(row.advance_date || "").slice(8, 10) === d);
      }
      const start = filters.offset;
      const page = rows.slice(start, start + PAGE_SIZE);
      setAdvancesPayload({ total: rows.length, advances: page, allRows: rows });
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load advances."));
    }
  }, [enabled, filters, toast]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await getOrgAdvances({
        limit: PAGE_SIZE,
        offset: historyFilters.offset,
        status: historyFilters.status || undefined,
      });
      setHistoryPayload({ total: data?.total || 0, advances: data?.advances || [] });
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load recovery history."));
    }
  }, [historyFilters, toast]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (tab === "advances") loadAdvances();
  }, [tab, loadAdvances]);

  useEffect(() => {
    if (tab === "recovery") loadHistory();
  }, [tab, loadHistory]);

  const cards = useMemo(() => {
    const o = overview || {};
    return [
      { label: "Enabled employees", value: o.enabled_employees ?? "—", accent: true },
      { label: "Pending approvals", value: o.pending_approvals ?? "—", warn: true },
      { label: "Total outstanding", value: formatMoney(o.total_outstanding) },
      { label: "Recovered this month", value: formatMoney(o.recovered_this_month) },
    ];
  }, [overview]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    const allocMap = new Map(allocations.map((a) => [String(a.employee_id), a]));
    return employees
      .map((emp) => {
        const id = emp.employee_id || emp.id;
        const alloc = allocMap.get(String(id));
        return {
          employee_id: id,
          employee_name:
            emp.full_name ||
            [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
            emp.employee_name ||
            "—",
          employee_code: emp.employee_code,
          department_name: emp.department_name || emp.department?.name,
          eligible: alloc ? Boolean(alloc.eligible) : false,
          outstanding_amount: alloc?.outstanding_amount ?? 0,
          status: alloc?.status || (alloc?.eligible ? "Active" : "Inactive"),
        };
      })
      .filter((row) => {
        if (!q) return true;
        return (
          String(row.employee_name).toLowerCase().includes(q) ||
          String(row.employee_code || "").toLowerCase().includes(q)
        );
      });
  }, [allocations, employees, empSearch]);

  async function toggleEnabled(next) {
    if (!canManage) return;
    setSaving(true);
    try {
      const res = await putAdvanceSettings(next);
      setEnabled(Boolean(res?.enabled));
      toast.success(next ? "Advance Taken enabled." : "Advance Taken disabled.");
      await loadCore();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to update advance settings."));
    } finally {
      setSaving(false);
    }
  }

  function openEligibility(row) {
    setEligDrawer({
      employee_id: row.employee_id,
      employee_name: row.employee_name,
      eligible: Boolean(row.eligible),
    });
  }

  async function saveEligibility(e) {
    e.preventDefault();
    if (!canManage || !enabled || !eligDrawer) return;
    setSaving(true);
    try {
      await putAdvanceEligibility(eligDrawer.employee_id, { eligible: eligDrawer.eligible });
      toast.success("Advance eligibility saved.");
      setEligDrawer(null);
      await loadCore();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to save eligibility."));
    } finally {
      setSaving(false);
    }
  }

  function openCreate() {
    setDuplicatePrompt(null);
    setCreateOpen(true);
  }

  function openEdit(row) {
    setDuplicatePrompt(null);
    setForm({
      employee_id: row.employee_id,
      amount: String(row.amount ?? ""),
      advance_date: String(row.advance_date || "").slice(0, 10),
      note: row.note || "",
    });
    setEditor({ mode: "edit", id: row.id, row });
  }

  function showAdvanceSuccess({ count = 1, isEdit = false, action = "create" } = {}) {
    if (action === "approve") {
      setSuccessFlash({
        title: "Advance approved successfully",
        message: "Approved amount will be deducted in the next payroll run for this employee.",
      });
      toast.success("Advance approved successfully.");
    } else if (action === "reject") {
      setSuccessFlash({
        title: "Advance rejected",
        message: "This advance will not be deducted in payroll.",
      });
      toast.success("Advance rejected.");
    } else if (action === "cancel") {
      setSuccessFlash({
        title: "Advance cancelled",
        message: "This pending advance will not be deducted in payroll.",
      });
      toast.success("Advance cancelled.");
    } else {
      setSuccessFlash({
        title: isEdit
          ? "Advance updated successfully"
          : count > 1
            ? `${count} advances successfully added`
            : "Advance successfully added",
        message: isEdit
          ? "Changes are saved. Approve before payroll deducts the amount."
          : count > 1
            ? "Entries are pending. Approve each advance before payroll deducts them."
            : "The advance is pending until HR / Admin approves it for payroll deduction.",
      });
      toast.success(
        isEdit
          ? "Advance updated successfully."
          : count > 1
            ? `${count} advances successfully added.`
            : "Advance successfully added."
      );
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function goToAdvancesList(advanceDate) {
    const month = monthFromDate(advanceDate);
    setFilters((f) => ({
      ...f,
      month,
      day: "",
      employee_id: "",
      status: "",
      offset: 0,
    }));
    setTab("advances");
  }

  function openDuplicatePrompt(record, opts = {}) {
    const {
      kind = "individual",
      payload = null,
      payloads = null,
      pendingId = null,
      reason = "same_day",
    } = opts;
    setDuplicatePrompt({
      kind,
      record,
      payload,
      payloads,
      pendingId,
      reason,
      employeeName: record?.employee_name || "this employee",
    });
    toast.error(
      kind === "approve"
        ? `Duplicate approved advance for ${record?.employee_name || "this employee"} on this date.`
        : `Duplicate advance for ${record?.employee_name || "this employee"}. Choose Allow duplicate to continue.`
    );
  }

  async function saveEditedAdvance(e) {
    e.preventDefault();
    if (!canManage || !enabled || editor?.mode !== "edit") return;
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Advance amount must be greater than 0.");
      return;
    }
    if (!form.advance_date) {
      toast.error("Advance date is required.");
      return;
    }
    setSaving(true);
    try {
      const existing = await findDuplicateAdvance({
        employeeId: form.employee_id,
        advanceDate: form.advance_date,
        amount,
        excludeId: editor.id,
        localAdvances: [...(advancesPayload.advances || []), ...(historyPayload.advances || [])],
      });
      if (existing) {
        openDuplicatePrompt(existing, {
          kind: "edit",
          payload: {
            amount,
            advance_date: form.advance_date,
            note: form.note.trim() || null,
          },
        });
        setSaving(false);
        return;
      }
      await updateAdvance(editor.id, {
        amount,
        advance_date: form.advance_date,
        note: form.note.trim() || null,
      });
      showAdvanceSuccess({ isEdit: true });
      setEditor(null);
      setForm(EMPTY_FORM);
      goToAdvancesList(form.advance_date);
      await loadCore();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to save advance."));
    } finally {
      setSaving(false);
    }
  }

  async function submitIndividualAdvance(payload, { allowDuplicate = false } = {}) {
    setSaving(true);
    try {
      if (!allowDuplicate) {
        const existing = await findDuplicateAdvance({
          employeeId: payload.employee_id,
          advanceDate: payload.advance_date,
          amount: payload.amount,
          localAdvances: [...(advancesPayload.advances || []), ...(historyPayload.advances || [])],
        });
        if (existing) {
          openDuplicatePrompt(existing, {
            kind: "individual",
            payload,
            reason: String(existing.status || "").toLowerCase() === "pending" ? "pending_exists" : "same_day",
          });
          return false;
        }
      }
      await createAdvance({
        employee_id: payload.employee_id,
        amount: payload.amount,
        advance_date: payload.advance_date,
        note: payload.note ?? null,
        allow_duplicate: Boolean(allowDuplicate),
      });
      showAdvanceSuccess({ count: 1 });
      setDuplicatePrompt(null);
      setCreateOpen(false);
      goToAdvancesList(payload.advance_date);
      await Promise.all([loadCore(), loadAdvances()]);
      return true;
    } catch (err) {
      // If user already confirmed duplicate, don't reopen the same dialog in a loop.
      if (!allowDuplicate) {
        const dup = advanceDuplicateFromError(err);
        if (dup?.existing) {
          openDuplicatePrompt(dup.existing, {
            kind: "individual",
            payload,
            reason: dup.reason || "same_day",
          });
          return false;
        }
      }
      toast.error(apiMessage(err, "Unable to save advance."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function submitBulkAdvances(payloads, { allowDuplicate = false } = {}) {
    if (!payloads?.length) {
      toast.error("No advances to submit.");
      return false;
    }
    setSaving(true);
    try {
      if (!allowDuplicate) {
        for (const row of payloads) {
          const existing = await findDuplicateAdvance({
            employeeId: row.employee_id,
            advanceDate: row.advance_date,
            amount: row.amount,
            localAdvances: [...(advancesPayload.advances || []), ...(historyPayload.advances || [])],
          });
          if (existing) {
            openDuplicatePrompt(existing, {
              kind: "bulk",
              payloads,
              reason: String(existing.status || "").toLowerCase() === "pending" ? "pending_exists" : "same_day",
            });
            return false;
          }
        }
      }

      let ok = 0;
      const errors = [];
      for (const row of payloads) {
        try {
          await createAdvance({
            employee_id: row.employee_id,
            amount: row.amount,
            advance_date: row.advance_date,
            note: row.note ?? null,
            allow_duplicate: Boolean(allowDuplicate),
          });
          ok += 1;
        } catch (err) {
          const dup = advanceDuplicateFromError(err);
          if (dup?.existing && !allowDuplicate) {
            openDuplicatePrompt(dup.existing, {
              kind: "bulk",
              payloads,
              reason: dup.reason || "same_day",
            });
            return false;
          }
          errors.push(apiMessage(err, "Failed"));
        }
      }
      if (ok) {
        showAdvanceSuccess({ count: ok });
        if (ok !== payloads.length) {
          toast.error(errors[0] || `${ok} of ${payloads.length} advances saved. Some failed.`);
        }
        setDuplicatePrompt(null);
        setCreateOpen(false);
        goToAdvancesList(payloads[0]?.advance_date);
        await loadCore();
        return true;
      }
      if (errors.length) toast.error(errors[0]);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function allowDuplicateAdvance(promptOverride = null) {
    const prompt = promptOverride || duplicatePrompt;
    if (!prompt?.record) return;
    setDuplicatePrompt(null);
    if (prompt.kind === "edit") {
      setSaving(true);
      try {
        await updateAdvance(editor.id, prompt.payload);
        showAdvanceSuccess({ isEdit: true });
        setEditor(null);
        setForm(EMPTY_FORM);
        goToAdvancesList(prompt.payload.advance_date);
        await Promise.all([loadCore(), loadAdvances()]);
      } catch (err) {
        toast.error(apiMessage(err, "Unable to save advance."));
      } finally {
        setSaving(false);
      }
      return;
    }
    if (prompt.kind === "approve" && prompt.pendingId) {
      await performApprove(prompt.pendingId, { allowDuplicate: true });
      return;
    }
    if (prompt.kind === "bulk") {
      await submitBulkAdvances(prompt.payloads, { allowDuplicate: true });
      return;
    }
    if (!prompt.payload) {
      toast.error("Missing advance details. Please try Save again.");
      return;
    }
    await submitIndividualAdvance(prompt.payload, { allowDuplicate: true });
  }

  async function ensureEligibleForAdvance(ids) {
    if (!ids?.length) return true;
    try {
      for (const id of ids) {
        await putAdvanceEligibility(id, { eligible: true });
      }
      await loadCore();
      return true;
    } catch (err) {
      toast.error(apiMessage(err, "Unable to enable employees for Advance Taken."));
      return false;
    }
  }

  const eligibleIds = useMemo(
    () => (allocations || []).filter((a) => a.eligible).map((a) => String(a.employee_id)),
    [allocations]
  );

  const calendarCells = useMemo(() => {
    const [y, m] = String(filters.month || currentMonthValue())
      .split("-")
      .map(Number);
    const firstDow = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const today = new Date();
    const todayKey =
      today.getFullYear() === y && today.getMonth() + 1 === m ? today.getDate() : null;
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push({ empty: true, key: `e-${i}` });
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({
        key: `d-${d}`,
        day: d,
        count: calendarDays[String(d)] || 0,
        isToday: todayKey === d,
        isSelected: String(filters.day) === String(d),
      });
    }
    return cells;
  }, [filters.month, filters.day, calendarDays]);

  async function performApprove(id, { allowDuplicate = false } = {}) {
    if (!canManage || !enabled) return;
    setSaving(true);
    try {
      if (!allowDuplicate) {
        const row = (advancesPayload.advances || []).find((a) => String(a.id) === String(id));
        if (row) {
          const approvedHit = await findDuplicateAdvance({
            employeeId: row.employee_id,
            advanceDate: row.advance_date,
            amount: row.amount,
            excludeId: row.id,
            localAdvances: advancesPayload.advances || [],
            approvedOnly: true,
          });
          if (approvedHit) {
            openDuplicatePrompt(approvedHit, { kind: "approve", pendingId: id });
            setSaving(false);
            return;
          }
        }
      }
      await approveAdvance(id, { allowDuplicate });
      showAdvanceSuccess({ action: "approve" });
      setDuplicatePrompt(null);
      await Promise.all([loadCore(), loadAdvances()]);
    } catch (err) {
      const dup = advanceDuplicateFromError(err);
      if (dup?.existing) {
        openDuplicatePrompt(dup.existing, { kind: "approve", pendingId: id });
        return;
      }
      toast.error(apiMessage(err, "Unable to approve advance."));
      await loadAdvances().catch(() => {});
    } finally {
      setSaving(false);
    }
  }

  async function onApprove(id) {
    await performApprove(id);
  }

  async function onReject(e) {
    e.preventDefault();
    if (!canManage || !rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }
    setSaving(true);
    try {
      await rejectAdvance(rejectTarget.id, rejectReason.trim());
      showAdvanceSuccess({ action: "reject" });
      setRejectTarget(null);
      setRejectReason("");
      await Promise.all([loadCore(), loadAdvances()]);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to reject advance."));
    } finally {
      setSaving(false);
    }
  }

  async function onCancel() {
    if (!canManage || !cancelTarget) return;
    setSaving(true);
    try {
      await cancelAdvance(cancelTarget.id);
      showAdvanceSuccess({ action: "cancel" });
      setCancelTarget(null);
      await Promise.all([loadCore(), loadAdvances()]);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to cancel advance."));
    } finally {
      setSaving(false);
    }
  }

  const breadcrumb = (
    <>
      <Link to="/settings">Settings</Link>
      <span>/</span>
      <Link to="/settings/allowances-benefits">Allowances & Benefits</Link>
      <span>/</span>
      <span>Advance Taken</span>
    </>
  );

  if (loading && !overview) {
    return (
      <div className="ab-page">
        <div className="ab-loading">Loading advance taken…</div>
      </div>
    );
  }

  return (
    <div className="ab-page">
      <FeatureHeader
        icon={IndianRupee}
        title="Advance Taken"
        subtitle="Salary advances recovered through payroll. Approve before deduction."
        breadcrumb={breadcrumb}
        actions={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" onClick={loadCore} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
            {canManage && enabled ? (
              <button type="button" className="ab-btn ab-btn--primary" onClick={() => openCreate()}>
                <Plus size={14} /> Add advance
              </button>
            ) : null}
          </>
        }
      />

      <SuccessFlash
        open={Boolean(successFlash)}
        title={successFlash?.title}
        message={successFlash?.message}
        onDismiss={() => setSuccessFlash(null)}
      />

      <FeatureDisabledBanner
        featureName="Advance Taken"
        managePath="/salary-v2/components"
        enabled={enabled}
        canManage={canManage}
      />

      {canManage ? (
        <div className="ab-panel">
          <div className="ab-panel__body">
            <OrgToggle
              label="Organisation Advance Taken"
              hint="When off, new advances and eligibility changes are blocked. Recovery history stays available."
              checked={enabled}
              disabled={saving}
              onChange={toggleEnabled}
            />
          </div>
        </div>
      ) : null}

      <SummaryCards items={cards} />

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "overview" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>How it works</h2>
              <p>Enable employees, record advances, approve, then recover in payroll.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <ol className="ab-note" style={{ paddingLeft: "1.1rem", display: "grid", gap: "0.45rem" }}>
              <li>Ensure the Advance Taken salary component is active for the organisation.</li>
              <li>Mark employees eligible under the Employees tab.</li>
              <li>Record an advance — it stays pending until approved.</li>
              <li>Approved advances are deducted once in the applicable payroll run.</li>
            </ol>
            <div className="ab-row-actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="ab-btn ab-btn--secondary" onClick={() => setTab("employees")}>
                Manage employees
              </button>
              <button type="button" className="ab-btn ab-btn--secondary" onClick={() => setTab("advances")}>
                Review advances
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tab === "employees" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>Employee eligibility</h2>
              <p>Control who can receive salary advances.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <div className="ab-toolbar">
              <div className="ab-field" style={{ minWidth: 200, flex: 1 }}>
                <label htmlFor="adv-emp-search">Search</label>
                <input
                  id="adv-emp-search"
                  className="ab-input"
                  placeholder="Name or code"
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                />
              </div>
              <label className="ab-check" style={{ marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={showAllAlloc}
                  onChange={(e) => setShowAllAlloc(e.target.checked)}
                />
                <span>
                  <strong>Show all employees</strong>
                  <small>Include non-eligible in allocation fetch</small>
                </span>
              </label>
            </div>
            {filteredEmployees.length === 0 ? (
              <EmptyState title="No employees found" description="Adjust search or ensure employees are active." />
            ) : (
              <div className="ab-table-wrap">
                <table className="ab-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Eligible</th>
                      <th>Outstanding</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((row) => (
                      <tr key={row.employee_id}>
                        <td>
                          <strong>{row.employee_name}</strong>
                          <div className="ab-note">{row.employee_code || "—"}</div>
                        </td>
                        <td>{row.department_name || "—"}</td>
                        <td>
                          <StatusBadge
                            status={row.eligible ? "eligible" : "inactive"}
                            label={row.eligible ? "Eligible" : "Not eligible"}
                          />
                        </td>
                        <td>{formatMoney(row.outstanding_amount)}</td>
                        <td>
                          {canManage ? (
                            <button
                              type="button"
                              className="ab-btn ab-btn--ghost ab-btn--sm"
                              disabled={!enabled}
                              onClick={() => openEligibility(row)}
                            >
                              Edit
                            </button>
                          ) : (
                            <span className="ab-note">View only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {tab === "advances" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>Advances</h2>
              <p>Browse by month calendar, then create, approve, reject, or cancel salary advances.</p>
            </div>
            {canManage && enabled ? (
              <button type="button" className="ab-btn ab-btn--primary" onClick={() => openCreate()}>
                <Plus size={14} /> Add advance
              </button>
            ) : null}
          </div>
          <div className="ab-panel__body">
            <div className="ab-month-bar">
              <div className="ab-month-nav">
                <button
                  type="button"
                  className="ab-btn ab-btn--secondary ab-btn--sm"
                  onClick={() => setFilters((f) => ({ ...f, month: shiftMonth(f.month, -1), day: "", offset: 0 }))}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={14} />
                </button>
                <strong>{monthLabel(filters.month)}</strong>
                <button
                  type="button"
                  className="ab-btn ab-btn--secondary ab-btn--sm"
                  onClick={() => setFilters((f) => ({ ...f, month: shiftMonth(f.month, 1), day: "", offset: 0 }))}
                  aria-label="Next month"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
              <div className="ab-row-actions">
                <button
                  type="button"
                  className="ab-btn ab-btn--ghost ab-btn--sm"
                  onClick={() => setFilters((f) => ({ ...f, month: currentMonthValue(), day: "", offset: 0 }))}
                >
                  This month
                </button>
                {filters.day ? (
                  <button
                    type="button"
                    className="ab-btn ab-btn--secondary ab-btn--sm"
                    onClick={() => setFilters((f) => ({ ...f, day: "", offset: 0 }))}
                  >
                    Clear day filter
                  </button>
                ) : null}
              </div>
            </div>

            <div className="ab-calendar" aria-label={`Calendar for ${monthLabel(filters.month)}`}>
              {DOW.map((d) => (
                <div key={d} className="ab-calendar__dow">
                  {d}
                </div>
              ))}
              {calendarCells.map((cell) =>
                cell.empty ? (
                  <button key={cell.key} type="button" className="ab-calendar__day" disabled />
                ) : (
                  <button
                    key={cell.key}
                    type="button"
                    className={`ab-calendar__day${cell.count ? " has-advances" : ""}${
                      cell.isSelected ? " is-selected" : ""
                    }${cell.isToday ? " is-today" : ""}`}
                    title={cell.count ? `${cell.count} advance(s)` : undefined}
                    onClick={() =>
                      setFilters((f) => ({
                        ...f,
                        day: String(f.day) === String(cell.day) ? "" : String(cell.day),
                        offset: 0,
                      }))
                    }
                  >
                    {cell.day}
                  </button>
                )
              )}
            </div>

            <div className="ab-toolbar">
              <div className="ab-field">
                <label>Status</label>
                <select
                  className="ab-select"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, offset: 0 }))}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="applied">Applied</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="ab-field" style={{ minWidth: 180 }}>
                <label>Employee</label>
                <select
                  className="ab-select"
                  value={filters.employee_id}
                  onChange={(e) => setFilters((f) => ({ ...f, employee_id: e.target.value, offset: 0 }))}
                >
                  <option value="">All employees</option>
                  {employees.map((emp) => {
                    const id = emp.employee_id || emp.id;
                    const name =
                      emp.full_name ||
                      [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
                      emp.employee_code;
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {advancesPayload.advances.length === 0 ? (
              <EmptyState
                icon={IndianRupee}
                title="No advances this month"
                description="Add an advance for an eligible employee, or adjust filters."
                action={
                  canManage && enabled ? (
                    <button type="button" className="ab-btn ab-btn--primary" onClick={() => openCreate()}>
                      Add advance
                    </button>
                  ) : null
                }
              />
            ) : (
              <>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Note</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advancesPayload.advances.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.employee_name || "—"}</strong>
                            <div className="ab-note">{row.employee_code || ""}</div>
                          </td>
                          <td>{formatDate(row.advance_date)}</td>
                          <td>{formatMoney(row.amount)}</td>
                          <td>
                            <StatusBadge status={displayStatusKey(row)} label={displayStatus(row)} />
                          </td>
                          <td>{row.rejection_reason || row.note || "—"}</td>
                          <td>
                            <div className="ab-row-actions">
                              {canManage && enabled && needsApproval(row) ? (
                                <>
                                  <button
                                    type="button"
                                    className="ab-btn ab-btn--primary ab-btn--sm"
                                    disabled={saving}
                                    onClick={() => onApprove(row.id)}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    className="ab-btn ab-btn--secondary ab-btn--sm"
                                    disabled={saving}
                                    onClick={() => {
                                      setRejectTarget(row);
                                      setRejectReason("");
                                    }}
                                  >
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    className="ab-btn ab-btn--ghost ab-btn--sm"
                                    onClick={() => openEdit(row)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="ab-btn ab-btn--ghost ab-btn--sm"
                                    onClick={() => setCancelTarget(row)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  total={advancesPayload.total}
                  limit={PAGE_SIZE}
                  offset={filters.offset}
                  onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      {tab === "recovery" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>Recovery / History</h2>
              <p>Applied, closed, and cancelled advances. Visible even when the feature is off.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <div className="ab-toolbar">
              <div className="ab-field">
                <label>Status</label>
                <select
                  className="ab-select"
                  value={historyFilters.status}
                  onChange={(e) =>
                    setHistoryFilters((f) => ({ ...f, status: e.target.value, offset: 0 }))
                  }
                >
                  <option value="">All</option>
                  <option value="applied">Applied</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            {historyPayload.advances.length === 0 ? (
              <EmptyState title="No recovery history" description="Try another status filter." />
            ) : (
              <>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyPayload.advances.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.employee_name || "—"}</strong>
                            <div className="ab-note">{row.employee_code || ""}</div>
                          </td>
                          <td>{formatDate(row.advance_date)}</td>
                          <td>{formatMoney(row.amount)}</td>
                          <td>
                            <StatusBadge status={displayStatusKey(row)} label={displayStatus(row)} />
                          </td>
                          <td>{row.rejection_reason || row.note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  total={historyPayload.total}
                  limit={PAGE_SIZE}
                  offset={historyFilters.offset}
                  onChange={(offset) => setHistoryFilters((f) => ({ ...f, offset }))}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      <Drawer
        open={Boolean(eligDrawer)}
        title="Advance eligibility"
        subtitle={eligDrawer?.employee_name}
        onClose={() => !saving && setEligDrawer(null)}
        footer={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={() => setEligDrawer(null)}>
              Cancel
            </button>
            <button type="submit" form="adv-elig-form" className="ab-btn ab-btn--primary" disabled={saving || !enabled}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {eligDrawer ? (
          <form id="adv-elig-form" onSubmit={saveEligibility}>
            <label className="ab-check">
              <input
                type="checkbox"
                checked={eligDrawer.eligible}
                onChange={(e) => setEligDrawer((d) => ({ ...d, eligible: e.target.checked }))}
              />
              <span>
                <strong>Eligible for salary advance</strong>
                <small>Only eligible employees can receive advances.</small>
              </span>
            </label>
          </form>
        ) : null}
      </Drawer>

      <AdvanceCreateWizard
        open={createOpen}
        enabled={enabled}
        saving={saving}
        employees={employees}
        eligibleIds={eligibleIds}
        onClose={() => {
          setDuplicatePrompt(null);
          setCreateOpen(false);
        }}
        onSubmitIndividual={submitIndividualAdvance}
        onSubmitBulk={submitBulkAdvances}
        onEnsureEligible={ensureEligibleForAdvance}
        toastWarn={(msg) => toast.error(msg)}
      />

      <Drawer
        open={Boolean(editor)}
        title="Edit advance"
        subtitle="Pending until approved. Only approved amounts are deducted in payroll."
        onClose={() => !saving && setEditor(null)}
        wide
        footer={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={() => setEditor(null)}>
              Cancel
            </button>
            <button type="submit" form="adv-form" className="ab-btn ab-btn--primary" disabled={saving || !enabled}>
              {saving ? "Saving…" : "Save advance"}
            </button>
          </>
        }
      >
        <form id="adv-form" className="ab-form-grid" onSubmit={saveEditedAdvance}>
          <div className="ab-field ab-field--full">
            <label>Employee</label>
            <input
              className="ab-input"
              disabled
              value={editor?.row?.employee_name || form.employee_id || ""}
            />
          </div>
          <div className="ab-field">
            <label>Amount</label>
            <input
              type="number"
              className="ab-input"
              min="0.01"
              step="0.01"
              required
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </div>
          <div className="ab-field">
            <label>Advance date</label>
            <input
              type="date"
              className="ab-input"
              required
              value={form.advance_date}
              onChange={(e) => setForm((f) => ({ ...f, advance_date: e.target.value }))}
            />
          </div>
          <div className="ab-field ab-field--full">
            <label>Reference / note</label>
            <input
              className="ab-input"
              maxLength={500}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Optional"
            />
          </div>
        </form>
      </Drawer>

      <Drawer
        open={Boolean(rejectTarget)}
        title="Reject advance"
        subtitle={rejectTarget ? formatMoney(rejectTarget.amount) : ""}
        onClose={() => !saving && setRejectTarget(null)}
        footer={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={() => setRejectTarget(null)}>
              Cancel
            </button>
            <button type="submit" form="adv-reject-form" className="ab-btn ab-btn--danger" disabled={saving}>
              {saving ? "Rejecting…" : "Reject advance"}
            </button>
          </>
        }
      >
        <form id="adv-reject-form" onSubmit={onReject}>
          <div className="ab-field">
            <label htmlFor="adv-reject-reason">Rejection reason</label>
            <textarea
              id="adv-reject-reason"
              className="ab-textarea"
              required
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        </form>
      </Drawer>

      <ConfirmShell
        open={Boolean(cancelTarget)}
        title="Cancel this salary advance?"
        message="This pending advance will not be deducted in payroll. The record is kept for audit."
        confirmLabel="Cancel advance"
        cancelLabel="Keep advance"
        danger
        loading={saving}
        onCancel={() => !saving && setCancelTarget(null)}
        onConfirm={onCancel}
      />

      <DuplicateRecordDialog
        open={Boolean(duplicatePrompt?.record)}
        title={
          duplicatePrompt?.kind === "approve"
            ? "Duplicate advance already approved"
            : duplicatePrompt?.reason === "pending_exists"
              ? "Pending advance already exists"
              : "Duplicate advance entry"
        }
        message={
          duplicatePrompt?.kind === "approve"
            ? `${duplicatePrompt?.employeeName || "This employee"} already has an approved advance on ${formatDate(
                duplicatePrompt?.record?.advance_date
              )}. Review it below before approving another for the same day.`
            : duplicatePrompt?.reason === "pending_exists"
              ? `${duplicatePrompt?.employeeName || "This employee"} already has a pending/open salary advance (${formatMoney(
                  duplicatePrompt?.record?.amount
                )} on ${formatDate(duplicatePrompt?.record?.advance_date)}). Choose Allow duplicate only if you intentionally want another entry.`
              : duplicatePrompt?.kind === "bulk"
                ? "At least one advance matches an existing record for the same employee and date. Review the match below."
                : `You are entering another advance for ${
                    duplicatePrompt?.employeeName || "this employee"
                  } on ${formatDate(duplicatePrompt?.record?.advance_date)}. Same employee and same date already exist.`
        }
        recordTitle={
          duplicatePrompt?.kind === "approve"
            ? "Already approved advance"
            : duplicatePrompt?.reason === "pending_exists"
              ? "Existing pending advance"
              : "Existing advance"
        }
        statusLabel={duplicatePrompt?.record ? displayStatus(duplicatePrompt.record) : undefined}
        statusKey={duplicatePrompt?.record ? displayStatusKey(duplicatePrompt.record) : undefined}
        fields={[
          {
            label: "Employee",
            value: duplicatePrompt?.record?.employee_name || duplicatePrompt?.employeeName || "—",
          },
          {
            label: "Advance date",
            value: formatDate(duplicatePrompt?.record?.advance_date),
          },
          {
            label: "Amount",
            value: formatMoney(duplicatePrompt?.record?.amount),
          },
          {
            label: "Reference",
            value: duplicatePrompt?.record?.note || "—",
          },
        ]}
        loading={saving}
        cancelLabel={duplicatePrompt?.kind === "approve" ? "Don't approve" : "Don't add"}
        allowLabel={
          duplicatePrompt?.kind === "approve"
            ? "Approve anyway"
            : duplicatePrompt?.kind === "bulk"
              ? "Allow duplicates"
              : "Allow duplicate"
        }
        onCancel={() => !saving && setDuplicatePrompt(null)}
        onAllow={() => {
          const snapshot = duplicatePrompt;
          void allowDuplicateAdvance(snapshot);
        }}
      />
    </div>
  );
}
